/**
 * <variant-links>
 *
 * Wraps a list of anchors pointing to sibling products (the "linked product variants" selector). Clicking an anchor
 * navigates natively; this element only makes that navigation feel instant by prefetching the target documents:
 *
 *   - on hover / touch / keyboard focus, the hovered target is prefetched right away;
 *   - once the page has loaded, every remaining target is prefetched one at a time during idle periods, so the
 *     queue never competes with the resources the current page still needs.
 */
(() => {
  if (window.customElements.get('variant-links')) {
    return;
  }

  const prefetched = new Set();

  const supportsLinkPrefetch = (() => {
    try {
      return document.createElement('link').relList.supports('prefetch');
    } catch (error) {
      return false;
    }
  })();

  const connectionAllowsPrefetch = () => {
    const connection = navigator.connection;

    if (!connection) {
      return true;
    }

    return !connection.saveData && !/(^|-)2g$/.test(connection.effectiveType || '');
  };

  const whenIdle = () => new Promise((resolve) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(resolve, { timeout: 1000 });
    } else {
      setTimeout(resolve, 200);
    }
  });

  const whenLoaded = () => new Promise((resolve) => {
    if (document.readyState === 'complete') {
      resolve();
    } else {
      window.addEventListener('load', resolve, { once: true });
    }
  });

  const prefetch = (url) => {
    const href = new URL(url, window.location.href).href;

    if (prefetched.has(href) || !connectionAllowsPrefetch()) {
      return Promise.resolve();
    }

    prefetched.add(href);

    if (!supportsLinkPrefetch) {
      // Safari does not implement rel="prefetch", so we warm the HTTP cache with a low priority request instead.
      return fetch(href, { credentials: 'same-origin', priority: 'low' }).catch(() => {});
    }

    return new Promise((resolve) => {
      const link = document.createElement('link');

      link.rel = 'prefetch';
      link.as = 'document';
      link.href = href;
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', resolve, { once: true });

      document.head.append(link);

      // Not every browser fires load or error for a prefetch link, and we must not stall the queue when it does not.
      setTimeout(resolve, 5000);
    });
  };

  class VariantLinks extends HTMLElement {
    #abortController = null;

    connectedCallback() {
      if (window.Shopify?.designMode) {
        return;
      }

      this.#abortController = new AbortController();

      const { signal } = this.#abortController;

      for (const eventName of ['pointerover', 'touchstart', 'focusin']) {
        this.addEventListener(eventName, this.#onIntent, { signal, passive: true });
      }

      this.#prefetchAll(signal);
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
    }

    get #links() {
      return Array.from(this.querySelectorAll('a[href]')).filter((link) => {
        // The anchor of the product we are already on has nothing to prefetch.
        return link.pathname !== window.location.pathname;
      });
    }

    #onIntent = (event) => {
      const link = event.target.closest('a[href]');

      if (link && this.contains(link)) {
        prefetch(link.href);
      }
    };

    async #prefetchAll(signal) {
      await whenLoaded();

      for (const link of this.#links) {
        if (signal.aborted) {
          return;
        }

        await whenIdle();

        if (signal.aborted) {
          return;
        }

        await prefetch(link.href);
      }
    }
  }

  window.customElements.define('variant-links', VariantLinks);
})();
